// A dónde puede ir un lanzamiento, y qué le exige cada destino a la ficha —
// ADR-2026-09-13-lanzamientos-para-vtex.
//
// Un lanzamiento se ARMA contra las reglas de su destino aunque todavía no se
// pueda publicar ahí: el cliente que prepara un lanzamiento para VTEX tiene que
// enterarse HOY de que le falta el peso del paquete, no el día que conectemos la
// tienda. Por eso las reglas viven acá, sin React y sin red, y las lee tanto la
// ficha (para avisar) como la validación de publicar (para frenar).
//
// De dónde salen los números:
//   · Tiendanube: `fichaTiendanube.ts` y su documentación (3 atributos, SEO 70/320).
//   · VTEX: el OpenAPI oficial del Catalog API (vtex/openapi-schemas) y el Help
//     Center. Producto `Name` ≤ 150; SKU `Name` ≤ 200, `RefId` ≤ 50 y único, y
//     `PackagedHeight/Length/Width/WeightKg` OBLIGATORIOS al crear el SKU; un
//     producto lleva UNA `CategoryId`; 50 SKUs por producto; `Title` ≤ 150 y
//     `MetaTagDescription` recomendado ≤ 150; `BrandId` o `BrandName` obligatorio.

export type CanalLanzamiento = "tiendanube" | "mercadolibre" | "vtex" | "redes" | "otro";

/**
 * Los valores que hoy acepta el `check` de `lanzamientos.canal`.
 *
 * `canales.test.ts` verifica que todo canal que se pueda armar esté en esta
 * lista — o sea que prender `sePuedeArmar` sin la migración rompe el test, no la
 * creación de un lanzamiento en producción. Fue lo que pasó con `otro`, que
 * entró en la base con `20260914120000_lanzamientos_canal_otro.sql`.
 */
export const CANALES_EN_BASE: readonly CanalLanzamiento[] = [
  "tiendanube",
  // 20260930160000_lanzamientos_a_varias_tiendas.sql (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
  "mercadolibre",
  "vtex",
  "redes",
  "otro",
];

/**
 * Los destinos se agrupan por a QUÉ se publica, no por plataforma: una tienda
 * online y una red social no compiten entre sí, y verlos en una lista plana de
 * cuatro obligaba a leer los cuatro para encontrar el que se busca.
 */
export type GrupoDeCanal = "tienda" | "otros";

export const GRUPOS: ReadonlyArray<{ id: GrupoDeCanal; titulo: string }> = [
  { id: "tienda", titulo: "Tienda online" },
  { id: "otros", titulo: "Otros destinos" },
];

export interface Canal {
  id: CanalLanzamiento;
  nombre: string;
  /** Lo que se lee debajo del nombre al elegir destino. */
  nota: string;
  grupo: GrupoDeCanal;
  /** ¿Se puede crear un lanzamiento para acá y cargarle productos? */
  sePuedeArmar: boolean;
  /** ¿Existe la conexión para publicarlo? Armar sin publicar es un estado válido. */
  sePuedePublicar: boolean;
}

export const CANALES: readonly Canal[] = [
  {
    id: "tiendanube",
    nombre: "Tienda Nube",
    nota: "Se crean o actualizan los productos en tu tienda",
    grupo: "tienda",
    sePuedeArmar: true,
    sePuedePublicar: true,
  },
  {
    id: "mercadolibre",
    nombre: "Mercado Libre",
    nota: "Se crean las publicaciones en tu cuenta",
    grupo: "tienda",
    sePuedeArmar: true,
    // Crea una publicación por color × talle (ADR-2026-09-30-crear-publicaciones-en-mercadolibre).
    sePuedePublicar: true,
  },
  {
    id: "vtex",
    nombre: "VTEX",
    nota: "Armalo ahora; la conexión llega pronto",
    grupo: "tienda",
    sePuedeArmar: true,
    sePuedePublicar: false,
  },
  {
    id: "otro",
    nombre: "Otra plataforma",
    nota: "Descargá un ZIP con las fotos en carpetas por categoría",
    grupo: "tienda",
    sePuedeArmar: true,
    // No hay tienda que conectar: el ZIP se arma en el navegador y siempre está
    // disponible. Lo que cambia no es SI se puede, sino QUÉ hace el botón, y eso
    // lo dice `salida` en las reglas.
    sePuedePublicar: true,
  },
  {
    id: "redes",
    nombre: "Instagram",
    nota: "Un calendario: qué sube, con qué texto y a qué hora",
    grupo: "otros",
    sePuedeArmar: true,
    // No hay un momento de publicar el lanzamiento entero: cada post sale a su
    // hora. Lo que decide si esa hora la ejecuta Atlas o sólo te avisa es
    // `modo_publicacion`, que es del lanzamiento y no del canal.
    sePuedePublicar: false,
  },
];

/**
 * Los canales ordenados por grupo, para dibujar la lista con sus subtítulos.
 *
 * Devuelve el grupo SOLO si tiene canales: un subtítulo sobre una lista vacía es
 * un encabezado que no encabeza nada. Vive acá y no en los dos selectores para
 * que el modal de "Nuevo lanzamiento" y el menú de "Enviar a lanzamiento" no
 * puedan mostrar dos listas distintas.
 */
export function canalesPorGrupo(
  canales: readonly Canal[] = CANALES,
): Array<{ id: GrupoDeCanal; titulo: string; canales: Canal[] }> {
  return GRUPOS.map((g) => ({
    ...g,
    canales: canales.filter((c) => c.grupo === g.id),
  })).filter((g) => g.canales.length > 0);
}

/**
 * Los destinos donde Atlas CREA los productos solo. Son los que se pueden combinar en un mismo
 * lanzamiento: si la marca tiene las dos cuentas, publica en las dos de una
 * (ADR-2026-09-30-lanzamiento-a-varias-tiendas). VTEX (sin conexión), el ZIP y el calendario de
 * Instagram van solos. Shopify se suma acá cuando exista.
 */
export const DESTINOS_QUE_CREAN: readonly CanalLanzamiento[] = ["tiendanube", "mercadolibre"];

export function creaProductos(canal: CanalLanzamiento): boolean {
  return DESTINOS_QUE_CREAN.includes(canal);
}

/**
 * Los destinos de un lanzamiento: los elegidos si son varios, o su `canal` de siempre. Un valor que
 * no conocemos se descarta (la base lo frena, pero la pantalla no tiene por qué romperse).
 */
export function destinosDe(l: { canal: CanalLanzamiento; destinos?: readonly string[] | null }): CanalLanzamiento[] {
  const elegidos = (l.destinos ?? []).filter((d): d is CanalLanzamiento => DESTINOS_QUE_CREAN.includes(d as CanalLanzamiento));
  return elegidos.length > 0 ? elegidos : [l.canal];
}

export function nombresDeDestinos(destinos: readonly CanalLanzamiento[]): string {
  const n = destinos.map(nombreCanal);
  return n.length <= 1 ? (n[0] ?? "") : `${n.slice(0, -1).join(", ")} y ${n[n.length - 1]}`;
}

export function canalDe(id: CanalLanzamiento): Canal {
  return CANALES.find((c) => c.id === id) ?? CANALES[0]!;
}

export function nombreCanal(id: CanalLanzamiento): string {
  return canalDe(id).nombre;
}

/**
 * Qué le exige el destino a la ficha de un producto. `null` en un tope = sin tope.
 *
 * Son reglas de la PLATAFORMA, no gustos nuestros: cada una tiene un motivo
 * concreto en el comentario, y si una cambia es porque cambió la documentación.
 */
export interface ReglasDelCanal {
  canal: CanalLanzamiento;
  /**
   * Qué hace el botón principal del lanzamiento: subir los productos a una
   * tienda, bajar un ZIP con las fotos ordenadas en carpetas
   * (ADR-2026-09-14-lanzamiento-a-carpetas), o ninguna de las dos —
   * `"redes"` no tiene botón porque no tiene un momento de publicar: cada post
   * lleva su propia fecha y hora
   * (ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts).
   *
   * Es además lo que decide QUÉ pantalla se dibuja: `"redes"` cambia la tabla
   * de productos por un calendario.
   */
  salida: "tienda" | "zip" | "redes";
  /**
   * El destino VENDE: precio, costo, stock, SKU y código de barras tienen
   * sentido. Apagado, la ficha no los muestra y la validación no los pide —
   * pedirle el precio a alguien que solo quiere ordenar sus fotos en carpetas es
   * pedirle un dato para tirarlo.
   */
  conPrecioYStock: boolean;
  /** El destino cotiza envíos: la sección de peso y medidas. */
  conEnvio: boolean;
  /** El destino tiene página de producto: marca, etiquetas y SEO. */
  conMarcaYSeo: boolean;
  /**
   * El destino RECIBE video, así que el selector de medios lo ofrece y se
   * pueden subir archivos de video.
   *
   * Apagado en las tiendas a propósito: ni Tiendanube ni VTEX aceptan un video
   * como foto de producto, y el publicador lo descartaría. Ofrecerlo ahí sería
   * dejar que alguien arme un lanzamiento con material que no va a llegar.
   */
  conVideo: boolean;
  /** Cuántos atributos de variante (Color, Talle…) admite un producto. */
  maxAtributos: number | null;
  /** Cuántas variantes admite un producto. */
  maxVariantes: number | null;
  /** Largo del nombre del producto. */
  maxNombre: number | null;
  /** Largo del SKU (código de referencia). */
  maxSku: number | null;
  maxSeoTitulo: number;
  maxSeoDescripcion: number;
  /**
   * Un producto va en UNA categoría, la más específica: la plataforma no tiene
   * "varias categorías por producto". Tildar una destilda las otras.
   */
  unaSolaCategoria: boolean;
  /**
   * Peso y medidas del paquete obligatorios: sin ellos la plataforma no deja
   * crear la variante, no es que "cotiza mal el envío".
   */
  envioObligatorio: boolean;
  /**
   * Cada variante necesita su propio SKU o código de barras, y los SKUs no se
   * pueden repetir: es la identidad de la variante en la plataforma.
   */
  skuOCodigoPorVariante: boolean;
  /**
   * La marca es obligatoria en la plataforma. Vacía, el conector usa el nombre
   * de la marca de Atlas, así que no frena: solo se avisa.
   */
  marcaConRespaldo: boolean;
  /**
   * Las fotos SIEMPRE van separadas por variante y el usuario no lo elige —
   * ADR-2026-09-14-en-el-zip-las-fotos-van-siempre-por-variante.
   *
   * En una tienda es una decisión real (Tiendanube muestra la primera foto de
   * cada variante, y hay productos donde una sola galería es mejor). En el ZIP
   * no: el árbol de carpetas ES la separación, y apagarlo amontona las fotos de
   * todos los colores en una carpeta sola, que es exactamente el trabajo que el
   * usuario venía a evitar.
   */
  imagenesPorVarianteFija: boolean;
}

/**
 * Si las fotos de este producto van separadas por variante.
 *
 * El destino puede imponerlo: ahí no se mira lo guardado, que puede venir
 * apagado de otro canal o de antes de que el interruptor desapareciera.
 */
/**
 * ¿La card "Configuración de variantes" tiene algo adentro?
 *
 * Sus tres interruptores dependen del destino, y exportando carpetas no queda
 * ninguno: el precio y el envío no existen ahí, y las fotos por variante son
 * fijas porque las carpetas SON la separación. Sin preguntarlo, la card abre
 * vacía — un menú que promete opciones y no tiene ninguna.
 */
export function hayConfiguracionDeVariantes(reglas: ReglasDelCanal): boolean {
  return reglas.conPrecioYStock || reglas.conEnvio || !reglas.imagenesPorVarianteFija;
}

export function separaImagenesPorVariante(
  guardado: boolean,
  reglas: ReglasDelCanal,
): boolean {
  return reglas.imagenesPorVarianteFija || guardado;
}

const TIENDANUBE: ReglasDelCanal = {
  canal: "tiendanube",
  salida: "tienda",
  conPrecioYStock: true,
  conEnvio: true,
  conMarcaYSeo: true,
  conVideo: false,
  // `attributes` admite hasta 3 por producto.
  maxAtributos: 3,
  maxVariantes: null,
  maxNombre: null,
  maxSku: null,
  maxSeoTitulo: 70,
  maxSeoDescripcion: 320,
  unaSolaCategoria: false,
  envioObligatorio: false,
  skuOCodigoPorVariante: false,
  marcaConRespaldo: false,
  imagenesPorVarianteFija: false,
};

const VTEX: ReglasDelCanal = {
  canal: "vtex",
  salida: "tienda",
  conPrecioYStock: true,
  conEnvio: true,
  conMarcaYSeo: true,
  conVideo: false,
  // Los atributos son "SKU specifications" de la categoría: no hay tope de cuántas.
  maxAtributos: null,
  // "There is a limit of 50 SKUs per product" (Help Center, Adding or editing SKUs).
  maxVariantes: 50,
  // Product `Name`: "Limited to 150 characters".
  maxNombre: 150,
  // SKU `RefId`: "The limit for the SKU RefId is 50 characters".
  maxSku: 50,
  // Product `Title`: "Limited to 150 characters".
  maxSeoTitulo: 150,
  // `MetaTagDescription`: "It is recommended not to exceed 150 characters".
  maxSeoDescripcion: 150,
  // El producto lleva UNA `CategoryId` (más su `DepartmentId`, que es la raíz del camino).
  unaSolaCategoria: true,
  // `PackagedHeight`, `PackagedLength`, `PackagedWidth` y `PackagedWeightKg` son
  // `required` al crear el SKU, y "Do not fill in this field with 0 or null".
  envioObligatorio: true,
  // SKU `RefId`: "Must be unique. Required only if Ean is not informed".
  skuOCodigoPorVariante: true,
  // "It is mandatory to use either BrandId or BrandName".
  marcaConRespaldo: true,
  imagenesPorVarianteFija: false,
};

/**
 * Mercado Libre (modelo nuevo: una publicación por color × talle, agrupadas en una familia) —
 * ADR-2026-09-30-lanzamiento-a-varias-tiendas.
 *
 * Lo propio de Mercado Libre que la ficha común no tiene (categoría de Mercado Libre, atributos
 * de la categoría, guía de talles, tipo de publicación) va en su sección aparte; acá sólo lo que
 * condiciona los campos compartidos:
 *   · `maxNombre` 60: el `family_name` (el nombre del producto en Mercado Libre) tiene el
 *     `max_title_length` de la categoría, 60 en moda.
 *   · `maxAtributos` 2: las variantes de una familia se distinguen por COLOR y TALLE.
 *   · `marcaConRespaldo`: BRAND es obligatoria; vacía, va la marca de Atlas.
 *   · `imagenesPorVarianteFija`: cada color es su propia publicación con sus propias fotos.
 *     Una sola galería para todos los colores publicaría la foto del negro en el rojo.
 */
const MERCADOLIBRE: ReglasDelCanal = {
  canal: "mercadolibre",
  salida: "tienda",
  conPrecioYStock: true,
  conEnvio: true,
  conMarcaYSeo: true,
  conVideo: false,
  maxAtributos: 2,
  maxVariantes: 100,
  maxNombre: 60,
  maxSku: null,
  maxSeoTitulo: TIENDANUBE.maxSeoTitulo,
  maxSeoDescripcion: TIENDANUBE.maxSeoDescripcion,
  unaSolaCategoria: false,
  envioObligatorio: false,
  skuOCodigoPorVariante: false,
  marcaConRespaldo: true,
  imagenesPorVarianteFija: true,
};

/**
 * Redes sociales: un calendario de posts, no una lista de productos —
 * ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts.
 *
 * Todo lo de la ficha de producto está apagado porque un post no tiene nada de
 * eso: no se vende desde ahí (no hay precio ni stock), no se envía (no hay peso
 * ni medidas) y no hay página de producto que posicionar (no hay marca ni SEO).
 * Un post tiene tres cosas —medios, copy y cuándo sale— y sus límites son los
 * de Instagram, que viven en `redes/post.ts` y no acá: éstas son las reglas de
 * una FICHA, y un post no tiene ficha.
 *
 * Heredaba las de Tiendanube como red de seguridad mientras el canal no se
 * podía armar. Ahora que se arma, heredarlas sería pedirle el peso del paquete
 * a una foto de Instagram.
 */
const REDES: ReglasDelCanal = {
  canal: "redes",
  salida: "redes",
  conPrecioYStock: false,
  conEnvio: false,
  conMarcaYSeo: false,
  // Lo único que enciende: es el destino donde un video ES el contenido.
  conVideo: true,
  maxAtributos: null,
  maxVariantes: null,
  maxNombre: null,
  maxSku: null,
  maxSeoTitulo: TIENDANUBE.maxSeoTitulo,
  maxSeoDescripcion: TIENDANUBE.maxSeoDescripcion,
  unaSolaCategoria: false,
  envioObligatorio: false,
  skuOCodigoPorVariante: false,
  marcaConRespaldo: false,
  imagenesPorVarianteFija: false,
};

/**
 * "Otra plataforma": no publica, exporta un ZIP de carpetas
 * (ADR-2026-09-14-lanzamiento-a-carpetas).
 *
 * La ficha se reduce a lo que el ZIP usa —nombre, categorías y variantes—, y no
 * hay topes de texto ni de variantes porque no hay una API del otro lado que los
 * imponga: los únicos límites son los de un sistema de archivos, y de esos se
 * ocupa `exportarZip.ts`.
 *
 * `unaSolaCategoria` SÍ, y es la decisión que hace que el ZIP tenga sentido: el
 * árbol de carpetas es el árbol de categorías, así que un producto va en un solo
 * lugar. Con dos categorías habría que elegir una igual o duplicar las fotos.
 *
 * `imagenesPorVarianteFija` SÍ, por lo mismo: las carpetas SON la separación por
 * variante (ADR-2026-09-14-en-el-zip-las-fotos-van-siempre-por-variante).
 */
const OTRO: ReglasDelCanal = {
  canal: "otro",
  salida: "zip",
  conPrecioYStock: false,
  conEnvio: false,
  conMarcaYSeo: false,
  conVideo: false,
  maxAtributos: null,
  maxVariantes: null,
  maxNombre: null,
  maxSku: null,
  maxSeoTitulo: TIENDANUBE.maxSeoTitulo,
  maxSeoDescripcion: TIENDANUBE.maxSeoDescripcion,
  unaSolaCategoria: true,
  envioObligatorio: false,
  skuOCodigoPorVariante: false,
  marcaConRespaldo: false,
  imagenesPorVarianteFija: true,
};

export function reglasDe(canal: CanalLanzamiento): ReglasDelCanal {
  switch (canal) {
    case "mercadolibre":
      return MERCADOLIBRE;
    case "vtex":
      return VTEX;
    case "redes":
      return REDES;
    case "otro":
      return OTRO;
    default:
      return TIENDANUBE;
  }
}

/**
 * Las reglas de un lanzamiento a VARIAS tiendas: gana la más exigente de cada una. La ficha es una
 * sola y tiene que pasar en todos los destinos (ADR-2026-09-30-lanzamiento-a-varias-tiendas):
 * un tope es el más chico, lo que una pide se pide, y lo que una impone se impone.
 */
export function reglasDeDestinos(destinos: readonly CanalLanzamiento[]): ReglasDelCanal {
  if (destinos.length <= 1) return reglasDe(destinos[0] ?? "tiendanube");
  const rs = destinos.map(reglasDe);
  const menor = (xs: Array<number | null>) => {
    const v = xs.filter((x): x is number => x !== null);
    return v.length > 0 ? Math.min(...v) : null;
  };
  const alguna = (f: (r: ReglasDelCanal) => boolean) => rs.some(f);
  return {
    canal: destinos[0]!,
    salida: "tienda",
    conPrecioYStock: alguna((r) => r.conPrecioYStock),
    conEnvio: alguna((r) => r.conEnvio),
    conMarcaYSeo: alguna((r) => r.conMarcaYSeo),
    conVideo: rs.every((r) => r.conVideo),
    maxAtributos: menor(rs.map((r) => r.maxAtributos)),
    maxVariantes: menor(rs.map((r) => r.maxVariantes)),
    maxNombre: menor(rs.map((r) => r.maxNombre)),
    maxSku: menor(rs.map((r) => r.maxSku)),
    maxSeoTitulo: Math.min(...rs.map((r) => r.maxSeoTitulo)),
    maxSeoDescripcion: Math.min(...rs.map((r) => r.maxSeoDescripcion)),
    unaSolaCategoria: alguna((r) => r.unaSolaCategoria),
    envioObligatorio: alguna((r) => r.envioObligatorio),
    skuOCodigoPorVariante: alguna((r) => r.skuOCodigoPorVariante),
    marcaConRespaldo: alguna((r) => r.marcaConRespaldo),
    imagenesPorVarianteFija: alguna((r) => r.imagenesPorVarianteFija),
  };
}

/**
 * Cómo se escribe la regla de categorías al pie del selector. Vive acá y no en
 * el componente porque es la explicación de una regla, y la regla está acá.
 */
export function ayudaDeCategorias(reglas: ReglasDelCanal): string {
  if (reglas.salida === "zip") {
    return "Es la carpeta donde van a quedar las fotos: una por categoría y otra por subcategoría. Va una sola por producto.";
  }
  return reglas.unaSolaCategoria
    ? "En VTEX un producto va en UNA categoría, la más específica. El departamento sale solo de dónde cuelga en el árbol."
    : "Es dónde lo encuentra el comprador navegando la tienda. Un producto puede estar en varias, y estar en una subcategoría no lo pone en su madre.";
}

export function ayudaDeEnvio(reglas: ReglasDelCanal): string {
  return reglas.envioObligatorio
    ? "VTEX no deja crear una variante sin el peso y las medidas del paquete. Son las del paquete cerrado: una prenda cuenta doblada."
    : "Tiendanube los usa para cotizar el envío. Si los dejás vacíos, el cliente puede ver un costo de envío incorrecto.";
}
